//PrimeReact
import { TabView, TabPanel } from "primereact/tabview"

//SOAR Context
import { UserContext } from "../../context/userContext"
import { LoaderMedium } from "../../components/Loaders/index"

//Housing components
import HousingAllocations from "./HousingAllocations/Index"
import { HousingActivities } from "./HousingActivities/HousingActivities"
import { useState } from "react"
import { housingApiListHousesPost } from "./housingApi"

//GraphQL
import { useLazyQuery } from "@apollo/client"
import { GET_PARTICIPANTS, GET_USERS } from "./queries"
import { useCallback } from "react"
import { LogError } from "../../helpers/logger"
import { useContext } from "react"
import { useEffect } from "react"
import HousingRent from "./HousingRent/HousingRent"
import HousingNotes from "./HousingNotes/HousingNotes"

export default function Housing() {
  const userCtx = useContext(UserContext)

  const inactiveStatuses = [
    "Inactive Discharged",
    "Inactive Referrals",
    "Kiosk Member",
  ]

  const [isLoadingHouses, setIsLoadingHouses] = useState(true)
  const [isLoadingSystemPrograms, setIsLoadingSystemPrograms] = useState(true)
  const [participant, setParticipant] = useState(undefined)
  const [systemPrograms, setSystemPrograms] = useState([]) //A list of SOAR programs
  const [systemActiveParticipants, setSystemActiveParticipants] = useState(null) //A list of SOAR active participants
  const [systemAllParticipants, setSystemAllParticipants] = useState(null) //A full list of historic participantIds for houses

  const [users, setUsers] = useState([])
  const [houses, setHouses] = useState([])
  const [houseOccupants, setHouseOccupants] = useState([]) //A complete list of house occupants
  const [houseActiveOccupants, setHouseActiveOccupants] = useState([]) //A complete list of house occupants
  const [houseParticipants, setHouseParticipants] = useState([]) //A list of all participants that have occupied a house
  const [houseActiveParticipants, setHouseActiveParticipants] = useState([]) //A list of all participants that have occupied a house within the last 48 hours onwards

  const [getUsers] = useLazyQuery(GET_USERS, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
    },
  })

  const [getParticipants] = useLazyQuery(GET_PARTICIPANTS, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
    },
  })

  useEffect(() => {
    if (!!systemAllParticipants && !!houseOccupants) {
      const filteredParticipants = systemAllParticipants.filter((participant) =>
        houseOccupants.some(
          (occupant) => occupant === participant.participant_id,
        ),
      )

      setHouseParticipants(filteredParticipants)
    }

    if (!!systemActiveParticipants && !!houseActiveOccupants) {
      const filteredActiveParticipants = systemActiveParticipants.filter(
        (participant) =>
          houseActiveOccupants.some(
            (occupant) => occupant === participant.participant_id,
          ),
      )

      setHouseActiveParticipants(filteredActiveParticipants)
    }
  }, [
    systemActiveParticipants,
    houseOccupants,
    houseActiveOccupants,
    systemAllParticipants,
  ])

  const getUserProfiles = useCallback(async () => {
    try {
      getUsers({
        variables: {
          staff_id: userCtx.session.user.staff_id,
          organization_id: userCtx.session.user.organization_id,
        },
      })
        .then((result) => {
          setUsers(result.data?.users)
        })
        .catch((error) => {
          LogError("Unable to retrieve user profiles from server", error)

          userCtx.setNotificationError(
            "Unable to retrieve user profiles, see console for details",
          )

          setUsers([])
        })

      let result = await getParticipants({
        variables: {
          staff_id: userCtx.session.user.staff_id,
          organization_id: userCtx.session.user.organization_id,
        },
      })

      const programs = result.data?.programs.filter(
        (program) => program.program_id_status === "ACTIVE",
      )
      setSystemPrograms(programs)
      setIsLoadingSystemPrograms(false)

      let allParts = result.data.participants.map((x) => {
        return {
          participant_id: x.participant_id,
          alias: x.participant_alias,
          status: x.participant_membership_status,
          statusActive: !inactiveStatuses.some(
            (status) => status === x.participant_membership_status,
          ),
          email: x.participant_email_account,
          phone: x.participant_phone_primary,
          optionName: `${x.participant_name_first} ${x.participant_name_last} ${
            x.participant_email_account
              ? ` [${x.participant_email_account}]`
              : ""
          }]`,
          name2: `${x.participant_name_first} ${x.participant_name_last} ${
            inactiveStatuses.some(
              (status) => status === x.participant_membership_status,
            )
              ? "[Inactive]"
              : ""
          }`,
          userRole: "Participants",
          participant_profile_image: x.participant_profile_image,
          demo_age: x.demo_age,
          demo_race: x.demo_race,
          demo_gender: x.demo_gender,
          demo_birthdate: x.demo_birthdate,
          demo_ethnicity: x.demo_ethnicity,
        }
      })

      let parts = result.data.participants
        .filter(
          (x) =>
            !inactiveStatuses.some(
              (status) => status === x.participant_membership_status,
            ),
        )
        .map((x) => {
          return {
            participant_id: x.participant_id,
            alias: x.participant_alias,
            status: x.participant_member_status,
            email: x.participant_email_account,
            phone: x.participant_phone_primary,
            optionName: `${x.participant_name_first} ${
              x.participant_name_last
            } ${
              x.participant_email_account
                ? ` [${x.participant_email_account}]`
                : ""
            }]`,
            name2: `${x.participant_name_first} ${x.participant_name_last}`,
            userRole: "Participants",
            participant_profile_image: x.participant_profile_image,
            programs: programs.filter((program) =>
              x.program_list.some((name) => name === program.program_id_name),
            ),
            demo_age: x.demo_age,
            demo_race: x.demo_race,
            demo_gender: x.demo_gender,
            demo_birthdate: x.demo_birthdate,
            demo_ethnicity: x.demo_ethnicity,
          }
        })

      setSystemActiveParticipants(parts)
      setSystemAllParticipants(allParts)
    } catch (error) {
      LogError("Unable to retrieve participant profiles from server", error)
      userCtx.setNotificationError(
        "Unable to retrieve participant profiles, see console for details",
      )
      setParticipant(undefined)
      setSystemActiveParticipants([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userCtx.session.user.organization_id,
    userCtx.setNotificationError,
    userCtx.session.user.staff_id,
  ])

  useEffect(() => [getUserProfiles()], [getUserProfiles])

  const getHouseList = useCallback(async () => {
    try {
      setIsLoadingHouses(true)

      let houseResult = await housingApiListHousesPost(
        userCtx.tokens.getHousingToken(),
        userCtx.session.user.enterprise_id,
        "All",
        1000,
        1,
      )

      const getHouseName = (house) => {
        let name = house.name
        let suffix = ""

        if (!!house.address) {
          suffix = house.address
        }

        if (!!house.city) {
          suffix = `${!!suffix ? suffix + ";" : ""}${house.city}`
        }

        return `${name} ${!!suffix ? ` [${suffix}]` : ""}`
      }

      const houseList = houseResult.houses.map((house) => {
        return {
          id: house.house.id,
          name: getHouseName(house.house),
          participants: house.participants,
          activeParticipants: house.activeParticipants,
        }
      })

      setHouses(houseList)
      setHouseOccupants(houseResult.participants)
      setHouseActiveOccupants(houseResult.activeParticipants)
      setIsLoadingHouses(false)
    } catch (error) {
      LogError("Unable to retrieve House list", error)
      userCtx.setNotificationError("Unable to retrieve houses")
      setHouses([])
      setIsLoadingHouses(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userCtx.session.user.enterprise_id,
    userCtx.setNotificationError,
    userCtx.tokens.getHousingToken,
  ])

  useEffect(() => {
    getHouseList()
  }, [getHouseList])

  if (isLoadingHouses) {
    return <LoaderMedium />
  }

  return (
    <>
      <TabView style={{ height: "100%" }}>
        <TabPanel header="Houses">
          <HousingAllocations
            currentParticipant={participant}
            onNewAllocationCreated={getHouseList}
            systemAllParticipants={systemAllParticipants}
            systemActiveParticipants={systemActiveParticipants}
            systemPrograms={systemPrograms}
            isLoadingPrograms={isLoadingSystemPrograms}
          />
        </TabPanel>
        <TabPanel header="Activities">
          <HousingActivities
            participant={participant}
            onSetParticipant={(selectedParticipant) => {
              setParticipant(selectedParticipant)
            }}
            houseParticipants={houseParticipants}
            activeHouseParticipants={houseActiveParticipants}
            users={users}
            houses={houses}
          />
        </TabPanel>
        <TabPanel header="Rent">
          <HousingRent
            participant={participant}
            users={users}
            onSetParticipant={(selectedParticipant) => {
              setParticipant(selectedParticipant)
            }}
            houseParticipants={houseParticipants}
            activeHouseParticipants={houseActiveParticipants}
            houses={houses}
          />
        </TabPanel>
        <TabPanel header="Notes">
          <HousingNotes
            participant={participant}
            onSetParticipant={(selectedParticipant) => {
              setParticipant(selectedParticipant)
            }}
            houseParticipants={houseParticipants}
            activeHouseParticipants={houseActiveParticipants}
            houses={houses}
            users={users}
          />
        </TabPanel>
      </TabView>
    </>
  )
}
